import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail, downloadReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { SearchSelect } from "@/components/SearchSelect";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Trash2, Pencil, FileDown, FileSpreadsheet, FileText, Tags } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", gst_number: "", address: "",
  contact_number_1: "", contact_number_2: "", category: "Groceries", opening_balance: 0,
};

export default function Suppliers() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [editCat, setEditCat] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [stmt, setStmt] = useState(null); // supplier for statement dialog
  const [stmtFrom, setStmtFrom] = useState("");
  const [stmtTo, setStmtTo] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const { data: cats = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/suppliers/categories")).data,
  });

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers", q, category],
    queryFn: async () =>
      (await api.get("/suppliers", { params: { q: q || undefined, category } })).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
      return editId ? api.put(`/suppliers/${editId}`, payload) : api.post("/suppliers", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success(editId ? "Supplier updated" : "Supplier added");
      setOpen(false);
      setForm(empty);
      setEditId(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/suppliers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Supplier deleted");
    },
  });

  const addCat = useMutation({
    mutationFn: async () => api.post("/suppliers/categories", { name: newCat.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["categories"] }); toast.success("Category added"); setNewCat(""); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delCat = useMutation({
    mutationFn: async (name) => api.delete("/suppliers/categories", { params: { name } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["categories"] }); toast.success("Category removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const renameCat = useMutation({
    mutationFn: async ({ oldName, name }) => api.put("/suppliers/categories", { old: oldName, name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Category renamed");
      setEditCat(null); setEditCatName("");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, gst_number: s.gst_number, address: s.address,
      contact_number_1: s.contact_number_1, contact_number_2: s.contact_number_2, category: s.category, opening_balance: s.opening_balance || 0 });
    setEditId(s.id); setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageTitle title="Supplier Details" subtitle={`${suppliers.length} suppliers`}>
        {isAdmin && <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="manage-categories"><Tags className="h-4 w-4" /> Categories</Button>}
        <Button variant="outline" onClick={() => downloadReport("/reports/suppliers/export", { fmt: "pdf", category }, "suppliers")} data-testid="export-pdf">
          <FileDown className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/suppliers/export", { fmt: "excel", category }, "suppliers")} data-testid="export-excel">
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </Button>
        {isAdmin && <Button onClick={openNew} data-testid="add-supplier-button">
          <Plus className="h-4 w-4" /> Add Supplier
        </Button>}
      </PageTitle>

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name, code, GST…" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9" data-testid="supplier-search" />
          </div>
          <div className="sm:w-56">
            <SearchSelect value={category} onChange={setCategory}
              options={[{ value: "all", label: "All Categories" }, ...cats.map((c) => ({ value: c, label: c }))]}
              searchPlaceholder="Type category…" testid="category-filter" />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>Contacts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : suppliers.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No suppliers yet.</TableCell></TableRow>
              ) : suppliers.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.supplier_code}`}>
                  <TableCell className="font-semibold text-primary">{s.supplier_code}</TableCell>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><Badge variant="secondary">{s.category}</Badge></TableCell>
                  <TableCell className="text-xs">{s.gst_number || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {[s.contact_number_1, s.contact_number_2].filter(Boolean).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Statement (PDF / Excel)" onClick={() => { setStmt(s); setStmtFrom(""); setStmtTo(""); }} data-testid={`statement-${s.supplier_code}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      {isAdmin && <><Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-${s.supplier_code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="text-destructive" data-testid={`delete-${s.supplier_code}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {s.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del.mutate(s.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog></>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
            <DialogDescription>Enter supplier account details. Supplier code is generated automatically.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Supplier Name *</Label>
              <Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="form-name" />
            </div>
            <div className="sm:col-span-2">
              <Label>Category</Label>
              <div className="mt-1.5">
                <SearchSelect value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                  options={cats.map((c) => ({ value: c, label: c }))}
                  placeholder="Select category" searchPlaceholder="Type category…" testid="form-category" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>GST Number</Label>
              <Input value={form.gst_number} onChange={set("gst_number")} className="mt-1.5" data-testid="form-gst" />
            </div>
            <div>
              <Label>Contact Number 1</Label>
              <Input value={form.contact_number_1} onChange={set("contact_number_1")} className="mt-1.5" data-testid="form-c1" />
            </div>
            <div>
              <Label>Contact Number 2</Label>
              <Input value={form.contact_number_2} onChange={set("contact_number_2")} className="mt-1.5" data-testid="form-c2" />
            </div>
            <div className="sm:col-span-2">
              <Label>Opening Balance (pre-app dues, ₹)</Label>
              <Input type="number" value={form.opening_balance} onChange={set("opening_balance")} placeholder="0" className="mt-1.5" data-testid="form-opening-balance" />
            </div>
            <div className="sm:col-span-2">
              <Label>Address</Label>
              <Textarea value={form.address} onChange={set("address")} className="mt-1.5" data-testid="form-address" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="save-supplier">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stmt} onOpenChange={(o) => !o && setStmt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supplier Statement</DialogTitle>
            <DialogDescription>{stmt?.supplier_code} — {stmt?.name}. Optionally pick a date window, then export.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>From</Label><div className="mt-1.5"><DatePicker value={stmtFrom} onChange={setStmtFrom} testid="stmt-from" /></div></div>
            <div><Label>To</Label><div className="mt-1.5"><DatePicker value={stmtTo} onChange={setStmtTo} testid="stmt-to" /></div></div>
          </div>
          <p className="text-xs text-muted-foreground">Leave dates blank for the full history. An opening balance row is added when a From date is set.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadReport(`/reports/supplier/${stmt.id}/statement`, { fmt: "excel", date_from: stmtFrom || undefined, date_to: stmtTo || undefined }, `statement_${stmt.supplier_code}`)} data-testid="stmt-excel">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
            <Button onClick={() => downloadReport(`/reports/supplier/${stmt.id}/statement`, { fmt: "pdf", date_from: stmtFrom || undefined, date_to: stmtTo || undefined }, `statement_${stmt.supplier_code}`)} data-testid="stmt-pdf">
              <FileDown className="h-4 w-4" /> PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Add, rename or remove supplier categories used across the app.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" data-testid="new-category-input" onKeyDown={(e) => { if (e.key === "Enter" && newCat.trim()) addCat.mutate(); }} />
            <Button onClick={() => addCat.mutate()} disabled={!newCat.trim() || addCat.isPending} data-testid="add-category-button"><Plus className="h-4 w-4" /> Add</Button>
          </div>
          <div className="max-h-64 overflow-y-auto ssacc-scroll divide-y">
            {cats.map((c) => (
              <div key={c} className="flex items-center justify-between gap-2 py-2" data-testid={`category-item-${c}`}>
                {editCat === c ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editCatName} onChange={(e) => setEditCatName(e.target.value)} className="h-8" data-testid={`rename-input-${c}`}
                      onKeyDown={(e) => { if (e.key === "Enter" && editCatName.trim()) renameCat.mutate({ oldName: c, name: editCatName.trim() }); }} />
                    <Button size="sm" onClick={() => renameCat.mutate({ oldName: c, name: editCatName.trim() })} disabled={!editCatName.trim() || renameCat.isPending} data-testid={`save-rename-${c}`}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditCat(null); setEditCatName(""); }}>Cancel</Button>
                  </div>
                ) : (
                  <span className="text-sm font-medium flex-1">{c}</span>
                )}
                <div className="flex gap-1">
                  {editCat !== c && (
                    <Button size="icon" variant="ghost" onClick={() => { setEditCat(c); setEditCatName(c); }} data-testid={`edit-category-${c}`}><Pencil className="h-4 w-4" /></Button>
                  )}
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delCat.mutate(c)} data-testid={`del-category-${c}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
